import React, { ReactNode, useCallback, useState, useEffect, useContext } from 'react';
import swal from 'sweetalert';
import AuthContext, { User, SignInCredentials } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface AuthState {
  token: string;
  user: User;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {

  const navigate = useNavigate();

  const [authenticated, setAuthenticated] = useState(false);
  const [profilePic, setProfilePic] = useState({ source: '' });

  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@PublisolPesquisasWebApp:token');
    const user = localStorage.getItem('@PublisolPesquisasWebApp:user');

    if (token && user) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
      setAuthenticated(true);
      return { token, user: JSON.parse(user) };
    }

    setAuthenticated(false);
    return {} as AuthState;
  });

  const signIn = useCallback(async ({ email, password }: SignInCredentials, navigate: (path: string) => void) => {
    
    try {

      const response = await api.post('auth', {
        email,
        password
      });
  
      const { token, user } = response.data;
  
      localStorage.setItem('@PublisolPesquisasWebApp:token', token);
      localStorage.setItem('@PublisolPesquisasWebApp:user', JSON.stringify(user));

      api.defaults.headers.Authorization = `Bearer ${token}`;

      setData({ token, user });
      setAuthenticated(true);
      
      navigate('/pesquisas');

    } catch (err: any) {
      setAuthenticated(false);

      if (err.response) {
        if (err.response.status === 401) {
          swal("Ooops! 401!", "E-mail ou senha incorretos ou inexistentes.", "error");
        }else {
          swal(
            `Ooops! ${ err.response.status }!`, 
            `Ocorreu um erro do tipo ${ err.response.status }.`, 
            "error"
          );
        }
      }else {
        swal("Ooops! :(",`${err}`, "error");
      }

    }

  }, []);

  const signOut = useCallback((navigate: (path: string) => void) => {
    localStorage.removeItem('@PublisolPesquisasWebApp:token');
    localStorage.removeItem('@PublisolPesquisasWebApp:user',);

    api.defaults.headers.Authorization = null;
    setAuthenticated(false);

    navigate('/');

    setData( {} as AuthState );
  }, []);

  const refreshToken = useCallback((newToken: string) => {

    localStorage.setItem('@PublisolPesquisasWebApp:token', newToken);
    api.defaults.headers.Authorization = `Bearer ${newToken}`;

    setData({ token: newToken, user: data.user });

  }, [data.user]);
  
  useEffect(() => {
    const getProfilePic = async() => {
      try {
        const response = await api.get(data.user.profile_pic, { responseType: 'arraybuffer' });
        const base64 = btoa(
          new Uint8Array(response.data).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            '',
          ),
        );
        setProfilePic({ source: "data:;base64," + base64 });
        
        const newToken = response.headers.token;
        if (newToken !== 'undefined') {
          refreshToken(newToken);
        }
      }catch(err: any) {
        if (err.response && err.response.status === 401) {
          signOut(navigate);
        }
      }
    }
    getProfilePic();
  }, [data, refreshToken, signOut]);

  return (
    <AuthContext.Provider value={
      { 
        token: data.token, 
        user: data.user, 
        authenticated, 
        profilePicBase64: profilePic,
        signIn, 
        signOut,
        refreshToken
      }
    }>
      { children }
    </AuthContext.Provider>
  );
};

export default AuthProvider;